import React, { useState, useEffect } from 'react';
import {
  Box,
  VStack,
  HStack,
  Text,
  Button,
  Badge,
  Image,
  SimpleGrid,
  Container,
  Heading,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  useColorModeValue,
  Spinner,
  Center,
  IconButton,
  Tooltip
} from '@chakra-ui/react';
import { FiTrendingUp, FiEye, FiHeart, FiPlay, FiRefreshCw } from 'react-icons/fi';
import { SearchDiscoveryService, type TrendingContent } from '../services/searchDiscoveryService';
import IconWrapper from './IconWrapper';
import { useNavigate } from 'react-router-dom';

interface TrendingContentProps {
  limit?: number;
  showHeader?: boolean;
}

const TrendingContentComponent: React.FC<TrendingContentProps> = ({
  limit = 20,
  showHeader = true
}) => {
  const navigate = useNavigate();
  const [trendingData, setTrendingData] = useState<{
    daily: TrendingContent[];
    weekly: TrendingContent[];
    monthly: TrendingContent[];
  }>({
    daily: [],
    weekly: [],
    monthly: []
  });
  const [activePeriod, setActivePeriod] = useState<'daily' | 'weekly' | 'monthly'>('weekly');
  const [isLoading, setIsLoading] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  useEffect(() => {
    loadTrendingContent();
  }, []);

  const loadTrendingContent = async () => {
    try {
      setIsLoading(true);
      const [daily, weekly, monthly] = await Promise.all([
        SearchDiscoveryService.getTrendingContent('daily', limit),
        SearchDiscoveryService.getTrendingContent('weekly', limit),
        SearchDiscoveryService.getTrendingContent('monthly', limit)
      ]);

      setTrendingData({ daily, weekly, monthly });
    } catch (error) {
      console.error('Error loading trending content:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await loadTrendingContent();
    setIsRefreshing(false);
  };

  const handleContentClick = (content: TrendingContent) => {
    if (content.category === 'short') {
      navigate(`/short/${content.id}`);
    } else {
      navigate(`/content/${content.id}`);
    }
  };

  const formatNumber = (num: number): string => {
    if (num >= 1000000) {
      return (num / 1000000).toFixed(1) + 'M';
    } else if (num >= 1000) {
      return (num / 1000).toFixed(1) + 'K';
    }
    return num.toString();
  };

  const getTrendingColor = (rank: number) => {
    if (rank <= 3) return 'red';
    if (rank <= 10) return 'orange';
    return 'gray';
  };

  const getTrendingIcon = (rank: number) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  const renderTrendingCard = (content: TrendingContent) => (
    <Box
      key={content.id}
      bg={cardBg}
      borderRadius="lg"
      overflow="hidden"
      border="1px"
      borderColor={borderColor}
      cursor="pointer"
      transition="all 0.2s"
      _hover={{ transform: 'translateY(-2px)', boxShadow: 'lg' }}
      onClick={() => handleContentClick(content)}
    >
      <Box position="relative">
        <Image
          src={content.poster}
          alt={content.title}
          w="100%"
          h="200px"
          objectFit="cover"
          fallbackSrc="/placeholder-poster.jpg"
        />
        <Badge
          position="absolute"
          top={2}
          left={2}
          colorScheme={getTrendingColor(content.rank)}
          fontSize="sm"
          px={2}
          py={1}
        >
          {getTrendingIcon(content.rank)}
        </Badge>
        <Badge
          position="absolute"
          top={2}
          right={2}
          colorScheme="purple"
          fontSize="xs"
        >
          {content.category}
        </Badge>
      </Box>
      
      <VStack p={4} align="stretch" gap={2}>
        <Text fontWeight="bold" fontSize="sm" noOfLines={2}>
          {content.title}
        </Text>
        
        <HStack justify="space-between" fontSize="xs" color="gray.500">
          <HStack>
            <IconWrapper icon={FiEye} size={12} />
            <Text>{formatNumber(content.views)}</Text>
          </HStack>
          <HStack>
            <IconWrapper icon={FiHeart} size={12} />
            <Text>{formatNumber(content.likes)}</Text>
          </HStack>
        </HStack>
        
        <HStack justify="space-between">
          <Badge colorScheme="green" fontSize="xs">
            Trending Score: {content.trendingScore.toFixed(1)}
          </Badge>
          <Button
            size="xs"
            colorScheme="purple"
            leftIcon={<IconWrapper icon={FiPlay} size={12} />}
            onClick={(e) => {
              e.stopPropagation();
              handleContentClick(content);
            }}
            whiteSpace="nowrap"
            minW="fit-content"
            px={3}
            flexShrink={0}
          >
            Watch
          </Button>
        </HStack>
      </VStack>
    </Box>
  );

  if (isLoading) {
    return (
      <Center py={8}>
        <Spinner size="lg" color="purple.400" />
      </Center>
    );
  }

  const currentData = trendingData[activePeriod];

  return (
    <Container maxW="container.xl">
      {showHeader && (
        <VStack gap={4} align="stretch" mb={6}>
          <HStack justify="space-between">
            <Heading size="lg" color="white">
              <IconWrapper icon={FiTrendingUp} size={24} style={{ marginRight: '8px' }} />
              Trending Content
            </Heading>
            <Tooltip label="Refresh trending data">
              <IconButton
                aria-label="Refresh"
                icon={<IconWrapper icon={FiRefreshCw} size={16} />}
                onClick={handleRefresh}
                isLoading={isRefreshing}
                variant="ghost"
                colorScheme="purple"
              />
            </Tooltip>
          </HStack>
          
          <Tabs
            variant="line"
            colorScheme="purple"
            onChange={(index) => {
              const periods: ('daily' | 'weekly' | 'monthly')[] = ['daily', 'weekly', 'monthly'];
              setActivePeriod(periods[index]);
            }}
          >
            <TabList>
              <Tab>Today</Tab>
              <Tab>This Week</Tab>
              <Tab>This Month</Tab>
            </TabList>
          </Tabs>
        </VStack>
      )}

      {currentData.length === 0 ? (
        <Center py={8}>
          <VStack gap={2}>
            <Text color="gray.500">No trending content found</Text>
            <Button size="sm" onClick={handleRefresh}>
              Try Again
            </Button>
          </VStack>
        </Center>
      ) : (
        <SimpleGrid columns={{ base: 2, md: 3, lg: 4, xl: 5 }} gap={4}>
          {currentData.map(renderTrendingCard)}
        </SimpleGrid>
      )}
    </Container>
  );
};

export default TrendingContentComponent;
